/**
 * CSI Pay design tokens: the single source of colour for the light and dark themes.
 * Taken from the AB#6872 preview (shared with COMPASS), with two light-mode status
 * colours darkened so 12px chip text meets WCAG AA (4.5:1) on its own background:
 * info #0277bd -> #01579b and warn #e65100 -> #b23c00.
 *
 * Pure module: no React or MUI imports, so tests and other pure helpers can use it.
 */

export type ColorMode = "light" | "dark";

/** The five status tones used by chips, callouts, KPI tiles and dialogs. */
export type StatusTone = "ok" | "info" | "warn" | "err" | "neutral";

/** Foreground (text/icon), background and border line for one status tone. */
export interface ToneTokens {
  fg: string;
  bg: string;
  line: string;
}

export interface DesignTokens {
  /** Brand blue (primary.main). */
  brand: string;
  /** Hover / pressed brand (primary.dark). In dark mode this is the lighter shade. */
  brandDark: string;
  /** Selected-row and active-nav tint (primary.light, palette.brandTint). */
  brandTint: string;
  /** Text on a brand-filled surface (primary.contrastText). */
  brandContrast: string;
  /** Page background (background.default). */
  page: string;
  /** Card / paper background (background.paper). */
  card: string;
  /** Borders and dividers (divider). */
  border: string;
  text: { primary: string; secondary: string; disabled: string };
  grey50: string;
  grey100: string;
  grey200: string;
  status: Record<StatusTone, ToneTokens>;
  /** Environment banner background (non-production builds). */
  env: string;
}

export const lightTokens: DesignTokens = {
  brand: "#406bab",
  brandDark: "#325385",
  brandTint: "#e8edf5",
  brandContrast: "#ffffff",
  page: "#f5f5f5",
  card: "#ffffff",
  border: "#e0e0e0",
  text: { primary: "#212121", secondary: "#616161", disabled: "#9e9e9e" },
  grey50: "#fafafa",
  grey100: "#f5f5f5",
  grey200: "#eeeeee",
  status: {
    ok: { fg: "#2e7d32", bg: "#e8f5e9", line: "#c8e6c9" },
    info: { fg: "#01579b", bg: "#e1f5fe", line: "#b3e5fc" },
    warn: { fg: "#b23c00", bg: "#fff3e0", line: "#ffcc80" },
    err: { fg: "#c62828", bg: "#fdecea", line: "#f5c2c0" },
    neutral: { fg: "#616161", bg: "#eeeeee", line: "#e0e0e0" },
  },
  env: "#c62828",
};

export const darkTokens: DesignTokens = {
  brand: "#688abd",
  brandDark: "#839fc9",
  brandTint: "#1f2c40",
  brandContrast: "#0b1220",
  page: "#121417",
  card: "#1c1f24",
  border: "#343a42",
  text: { primary: "#e6e8eb", secondary: "#b3b9c2", disabled: "#8a929d" },
  grey50: "#22262c",
  grey100: "#1a1d21",
  grey200: "#2c3138",
  status: {
    ok: { fg: "#81c995", bg: "#16291c", line: "#2c5236" },
    info: { fg: "#7cc4f0", bg: "#10273a", line: "#1f4a68" },
    warn: { fg: "#f5b971", bg: "#33240f", line: "#63451a" },
    err: { fg: "#f2a3a3", bg: "#3a1a1c", line: "#6e2c30" },
    neutral: { fg: "#c2c7ce", bg: "#2a2f36", line: "#3b424b" },
  },
  env: "#c62828",
};

export const tokens: Record<ColorMode, DesignTokens> = {
  light: lightTokens,
  dark: darkTokens,
};

/** Monospace stack for IDs, masked tax IDs and codes. */
export const monoFontFamily =
  '"Roboto Mono", ui-monospace, Menlo, Consolas, monospace';
